import { useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UserContext from "../../../contexts/UserContext";
import { useGetOrgsQuery } from "../../../services/orgs";
import { useFetchAllUsersQuery } from "../../../services/payments";
import { findRaasAccountDefaultJoid } from "../identity";
import { getRaasExecProjectPlatformType } from "../platform";
import {
  getRaasPortalDefinition,
  hasRaasPortalToolbarControl,
} from "../portalConfig";

export const useRaasPortalIdentity = () => {
  const userModel = useContext(UserContext);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const currentUser = userModel.getUser();
  const currentUserId = currentUser.uid;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const portalDefinition = useMemo(
    () => getRaasPortalDefinition({ pathname }),
    [pathname],
  );
  const canUseImpersonationSelector =
    hasRaasPortalToolbarControl({
      controlKey: "impersonation_selector",
      portalDefinition,
    }) && Boolean(userModel.canImpersonate?.());
  const canUseOrgSelector =
    canUseImpersonationSelector &&
    hasRaasPortalToolbarControl({
      controlKey: "org_selector",
      portalDefinition,
    });
  const targetUserId = canUseImpersonationSelector ? searchParams.get("joid") : null;
  const selectedAccountId = searchParams.get("account_id");
  const currentUserAccountId = currentUser.account_id;
  const routeJoid = targetUserId || currentUserId;
  const isTargetUserImpersonation = Boolean(
    targetUserId && targetUserId !== currentUserId,
  );
  const shouldFetchAllUsers = Boolean(
    routeJoid && (canUseImpersonationSelector || isTargetUserImpersonation),
  );
  const {
    data: allOrgs,
    isFetching: isFetchingAllOrgs,
    isLoading: isLoadingAllOrgs,
  } = useGetOrgsQuery(
    { joid: routeJoid },
    { skip: !canUseOrgSelector || !routeJoid },
  );
  const {
    data: allUsers,
    isFetching: isFetchingAllUsers,
    isLoading: isLoadingAllUsers,
  } = useFetchAllUsersQuery(
    { joid: routeJoid },
    { skip: !shouldFetchAllUsers },
  );
  const selectedAccountDefaultJoid = useMemo(() => {
    if (
      !canUseOrgSelector ||
      !selectedAccountId ||
      selectedAccountId === currentUserAccountId
    ) {
      return undefined;
    }

    return findRaasAccountDefaultJoid({
      accountId: selectedAccountId,
      allOrgs,
      allUsers,
    });
  }, [
    allOrgs,
    allUsers,
    canUseOrgSelector,
    currentUserAccountId,
    selectedAccountId,
  ]);
  const needsSelectedAccountJoid =
    canUseOrgSelector &&
    Boolean(selectedAccountId) &&
    selectedAccountId !== currentUserAccountId &&
    !targetUserId;
  const isSelectedAccountDefaultPending =
    needsSelectedAccountJoid &&
    !selectedAccountDefaultJoid &&
    (isFetchingAllOrgs ||
      isFetchingAllUsers ||
      isLoadingAllOrgs ||
      isLoadingAllUsers);
  const joid =
    targetUserId ||
    selectedAccountDefaultJoid ||
    (isSelectedAccountDefaultPending ? "" : currentUserId);
  const targetUser = useMemo(
    () => allUsers?.users.find((user) => user._id === targetUserId),
    [allUsers, targetUserId],
  );
  const targetOrgUser = useMemo(
    () => allOrgs?.find((user) => user.id === targetUserId),
    [allOrgs, targetUserId],
  );
  const platformType = getRaasExecProjectPlatformType({
    currentUserIsRra: userModel.isRRAUser(),
    isTargetUserImpersonation,
    targetUserEmail: targetUser?.local?.email || targetOrgUser?.email,
  });
  const isProjectPlatformResolutionPending =
    isSelectedAccountDefaultPending ||
    (isTargetUserImpersonation && !targetUser && !targetOrgUser);

  useEffect(() => {
    if (!joid || searchParams.get("joid") === joid) return;

    const nextSearchParams = new URLSearchParams(search);
    nextSearchParams.set("joid", joid);
    navigate(
      {
        pathname,
        search: `?${nextSearchParams.toString()}`,
      },
      { replace: true },
    );
  }, [joid, navigate, pathname, search, searchParams]);

  return {
    allOrgs,
    allUsers,
    canUseImpersonationSelector,
    canUseOrgSelector,
    currentUser,
    isFetchingAllOrgs,
    isFetchingAllUsers,
    isProjectPlatformResolutionPending,
    joid,
    platformType,
    searchParams,
    selectedAccountDefaultJoid,
    selectedAccountId,
    selectedJoid: targetUserId,
  };
};
